/**
 * parseRoomNumber — extract a plausible room number from a spoken-text
 * transcript. Pure: testable from Vitest with no browser globals.
 *
 * Cleaners say things like "two thirteen", "room 213", "1st floor 213",
 * "two-one-three". The Web Speech API typically transcribes spoken
 * digits as digits (Chrome / Edge / Android Chrome), so the dominant
 * shape we extract from is a 3-4 digit run somewhere in the transcript.
 *
 * Validity matches the picker's own rules:
 *   - hundreds digit ≥ 1 (must encode a floor)
 *   - last two digits in 1..24 (24 doors per floor convention)
 *
 * Returns null if no plausible room number is present so the UI can
 * either re-prompt or fall back to typing.
 */

const ROOM_NUMBER_RE = /(?:^|\D)([1-9]\d{2,3})(?:\D|$)/
const ROOM_OFFSET_MIN = 1
const ROOM_OFFSET_MAX = 24

export interface ParsedRoomVoice {
  /** The extracted room number as a string (canonical form), or null. */
  roomNumber: string | null
}

export function parseRoomNumber(transcript: string): ParsedRoomVoice {
  if (typeof transcript !== 'string') return { roomNumber: null }
  const cleaned = transcript.toLowerCase().replace(/[,–—]/g, ' ').trim()
  if (cleaned.length === 0) return { roomNumber: null }

  // Walk every 3-4 digit run; first one whose offset is in the
  // documented 1..24 range wins. Skips numbers that look like floor-only
  // ("1st floor" — captured as "1" not 3-4 digits anyway) and numbers
  // outside the discoverable picker range.
  const re = new RegExp(ROOM_NUMBER_RE.source, 'g')
  let match: RegExpExecArray | null
  while ((match = re.exec(cleaned)) !== null) {
    const candidate = match[1]
    const num = Number.parseInt(candidate, 10)
    const offset = num % 100
    if (offset >= ROOM_OFFSET_MIN && offset <= ROOM_OFFSET_MAX) {
      return { roomNumber: candidate }
    }
  }
  return { roomNumber: null }
}
